/**
 * Notification feed — pure presentation helpers (PROD-383).
 *
 * House pattern (`lib/auto-reload-alert.ts`): components render, this module
 * speaks. The adapter feeds `@ordo/ui/notification-list-page`'s item shape on
 * the /notifications page; the dot class colors the bell rows by how much a
 * category matters (money problems are amber, everything else muted).
 */

import type { NotificationListItem } from '@ordo/ui/notification-list-page'

import type { NotificationFeedItem } from '@/api/notifications-feed'

/** Categories that mean "money moved or will stop moving" — amber dots. */
const MONEY_CATEGORIES = new Set(['PAYMENT_FAILED', 'BALANCE_CLAWBACK', 'ACCOUNT_LOW_BALANCE', 'ORDER_FAILED'])

export function notificationDotClass(category: string): string {
  return MONEY_CATEGORIES.has(category) ? 'bg-amber-500' : 'bg-muted-foreground/40'
}

/** Relative "3 hours ago" label; empty string for unparseable input. */
export function notificationTimestamp(iso: string, now: Date = new Date()): string {
  const timestamp = new Date(iso).getTime()
  if (Number.isNaN(timestamp)) {
    return ''
  }
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  const seconds = Math.round((timestamp - now.getTime()) / 1000)
  if (Math.abs(seconds) < 60) {
    return formatter.format(seconds, 'second')
  }
  const minutes = Math.round(seconds / 60)
  if (Math.abs(minutes) < 60) {
    return formatter.format(minutes, 'minute')
  }
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) {
    return formatter.format(hours, 'hour')
  }
  return formatter.format(Math.round(hours / 24), 'day')
}

/**
 * Adapter for `NotificationListPage`. Ordo notifications are
 * system-authored statements ("Wallet auto-reload payment failed"), not
 * actor-verb-object events like customers' mentions — so the title is the
 * action text and the body is the target line.
 */
export function toOrdoNotificationCenterItem(notification: NotificationFeedItem): NotificationListItem {
  return {
    id: notification.id,
    unread: !notification.readAt,
    actor: notification.actor ? { name: notification.actor.name, image: notification.actor.image } : null,
    actionText: notification.title,
    targetText: notification.body ?? undefined,
    timestamp: notificationTimestamp(notification.createdAt),
    openLabel: notification.deepLinkUri ? 'Open' : undefined,
  }
}
